import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import AgentLayout from '../../components/layout/AgentLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '../../components/ui/dropdown-menu';
import {
  Search,
  CheckCircle,
  Clock,
  AlertCircle,
  FileText,
  User,
  Target,
  GraduationCap,
  Plane,
  Home,
  DollarSign,
  Briefcase,
  RefreshCw,
  Loader2,
  Calendar,
  MoreVertical,
  Award,
  AlertTriangle,
  Timer,
  Filter,
  ExternalLink,
  CheckSquare,
  Upload,
  Users,
  BookOpen,
  Edit,
  Zap,
  ListTodo,
  CalendarClock,
  Flame
} from 'lucide-react';
import { agentAPI } from '../../services/api';
import { useRoleBasePath } from '../../hooks/useRoleBasePath';
import { toast } from 'sonner';
import { format, formatDistanceToNow, isPast, isToday, isTomorrow, startOfDay, endOfDay } from 'date-fns';
import { cn } from '../../lib/utils';

// Service type configuration
const SERVICE_TYPE_CONFIG = {
  PROFILE_ASSESSMENT: { label: 'Profile Assessment', icon: Target, color: 'bg-blue-50', textColor: 'text-blue-700' },
  UNIVERSITY_SHORTLISTING: { label: 'Preapplication Support', icon: GraduationCap, color: 'bg-purple-50', textColor: 'text-purple-700' },
  APPLICATION_ASSISTANCE: { label: 'Apply University', icon: FileText, color: 'bg-pink-50', textColor: 'text-pink-700' },
  VISA_GUIDANCE: { label: 'Visa & Interview Support', icon: Plane, color: 'bg-orange-50', textColor: 'text-orange-700' },
  SCHOLARSHIP_SEARCH: { label: 'Ticket & Travel Support', icon: Award, color: 'bg-green-50', textColor: 'text-green-700' },
  LOAN_ASSISTANCE: { label: 'Education Loan', icon: DollarSign, color: 'bg-emerald-50', textColor: 'text-emerald-700' },
  ACCOMMODATION_HELP: { label: 'Find Accommodation', icon: Home, color: 'bg-cyan-50', textColor: 'text-cyan-700' },
  PRE_DEPARTURE_ORIENTATION: { label: 'Find Jobs Abroad', icon: Briefcase, color: 'bg-indigo-50', textColor: 'text-indigo-700' }
};

// Task status configuration
const TASK_STATUS_CONFIG = {
  PENDING: { label: 'Pending', color: 'bg-gray-100 text-gray-700', bgColor: 'bg-gray-50' },
  IN_PROGRESS: { label: 'In Progress', color: 'bg-blue-100 text-blue-700', bgColor: 'bg-blue-50' },
  SUBMITTED: { label: 'Submitted', color: 'bg-purple-100 text-purple-700', bgColor: 'bg-purple-50' },
  UNDER_REVIEW: { label: 'Under Review', color: 'bg-amber-100 text-amber-700', bgColor: 'bg-amber-50' },
  REVISION_REQUIRED: { label: 'Revision Required', color: 'bg-orange-100 text-orange-700', bgColor: 'bg-orange-50' },
  COMPLETED: { label: 'Completed', color: 'bg-green-100 text-green-700', bgColor: 'bg-green-50' }
};

// Task type configuration
const TASK_TYPE_CONFIG = {
  DOCUMENT_UPLOAD: { label: 'Document Upload', icon: Upload },
  QUESTIONNAIRE: { label: 'Questionnaire', icon: FileText },
  VIDEO_CALL: { label: 'Video Call', icon: Users },
  REVIEW_SESSION: { label: 'Review Session', icon: BookOpen },
  INFORMATION_SUBMISSION: { label: 'Information Submission', icon: Edit },
  FORM_COMPLETION: { label: 'Form Completion', icon: FileText },
  PAYMENT: { label: 'Payment', icon: DollarSign },
  APPROVAL_REQUIRED: { label: 'Approval Required', icon: CheckCircle },
  OTHER: { label: 'Other', icon: Zap }
};

// Priority configuration
const PRIORITY_CONFIG = {
  LOW: { label: 'Low', color: 'text-gray-500', bgColor: 'bg-gray-100' },
  MEDIUM: { label: 'Medium', color: 'text-blue-500', bgColor: 'bg-blue-100' },
  HIGH: { label: 'High', color: 'text-orange-500', bgColor: 'bg-orange-100', icon: AlertTriangle },
  URGENT: { label: 'Urgent', color: 'text-red-500', bgColor: 'bg-red-100', icon: Flame }
};

// Task Row Component
const TaskRow = ({ task, onComplete, onOpenCase }) => {
  const navigate = useNavigate();
  const basePath = useRoleBasePath();
  const [completing, setCompleting] = useState(false);

  const taskTypeConfig = TASK_TYPE_CONFIG[task.taskType] || TASK_TYPE_CONFIG.OTHER;
  const TaskIcon = taskTypeConfig.icon;
  const statusConfig = TASK_STATUS_CONFIG[task.status] || {};
  const priorityConfig = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.MEDIUM;
  const PriorityIcon = priorityConfig.icon;
  const serviceConfig = SERVICE_TYPE_CONFIG[task.serviceType] || {};

  const isOverdue = task.dueDate && isPast(new Date(task.dueDate)) && task.status !== 'COMPLETED';
  const isDueToday = task.dueDate && isToday(new Date(task.dueDate));
  const isDueTomorrow = task.dueDate && isTomorrow(new Date(task.dueDate));

  const handleComplete = async (e) => {
    e.stopPropagation();
    setCompleting(true);
    try {
      await onComplete(task.taskId);
    } finally {
      setCompleting(false);
    }
  };

  const handleOpenCase = () => {
    navigate(`${basePath}/service-requests/${task.serviceRequestId}`);
  };

  const getDeadlineDisplay = () => {
    if (!task.dueDate) return <span className="text-gray-400">No deadline</span>;
    const deadlineDate = new Date(task.dueDate);

    if (isOverdue) {
      return (
        <span className="text-red-600 font-medium flex items-center gap-1">
          <AlertCircle className="w-3 h-3" />
          Overdue
        </span>
      );
    }
    if (isDueToday) {
      return <span className="text-orange-600 font-medium">Due Today</span>;
    }
    if (isDueTomorrow) {
      return <span className="text-amber-600 font-medium">Due Tomorrow</span>;
    }
    return <span className="text-gray-600">{format(deadlineDate, 'MMM d')}</span>;
  };

  return (
    <div
      className={cn(
        "group flex items-center gap-4 p-4 border rounded-lg hover:shadow-md transition-all cursor-pointer",
        isOverdue && "border-red-200 bg-red-50/30",
        task.status === 'COMPLETED' && "bg-gray-50 opacity-75",
        (task.priority === 'HIGH' || task.priority === 'URGENT') && !isOverdue && "border-orange-200"
      )}
      onClick={handleOpenCase}
    >
      {/* Checkbox / Complete Button */}
      <div onClick={(e) => e.stopPropagation()}>
        {task.status !== 'COMPLETED' ? (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 rounded-full border-2 border-gray-300 hover:border-emerald-500 hover:bg-emerald-50"
            onClick={handleComplete}
            disabled={completing}
          >
            {completing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <CheckCircle className="w-4 h-4 opacity-0 group-hover:opacity-100 text-emerald-500 transition-opacity" />
            )}
          </Button>
        ) : (
          <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center">
            <CheckCircle className="w-5 h-5 text-green-600" />
          </div>
        )}
      </div>

      {/* Task Icon */}
      <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center", statusConfig.bgColor)}>
        <TaskIcon className="w-5 h-5 text-gray-600" />
      </div>

      {/* Task Details */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h4 className={cn(
            "font-medium text-gray-900 truncate",
            task.status === 'COMPLETED' && "line-through text-gray-500"
          )}>
            {task.title}
          </h4>
          {PriorityIcon && (
            <PriorityIcon className={cn("w-4 h-4", priorityConfig.color)} />
          )}
        </div>
        <p className="text-sm text-gray-500 truncate">{task.description}</p>
      </div>

      {/* Student */}
      <div className="flex items-center gap-2 min-w-[180px]">
        <Avatar className="h-8 w-8">
          <AvatarImage src={task.student?.avatar} />
          <AvatarFallback className="bg-emerald-100 text-emerald-700 text-xs">
            {task.student?.firstName?.[0]}{task.student?.lastName?.[0]}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">
            {task.student?.firstName} {task.student?.lastName}
          </p>
          <Badge className={cn("text-xs", serviceConfig.color, serviceConfig.textColor)}>
            {serviceConfig.label || task.serviceType}
          </Badge>
        </div>
      </div>

      {/* Status */}
      <Badge className={cn("min-w-[100px] justify-center", statusConfig.color)}>
        {statusConfig.label}
      </Badge>

      {/* Deadline */}
      <div className="min-w-[100px] text-sm text-center">
        {getDeadlineDisplay()}
      </div>

      {/* Actions */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
          <Button variant="ghost" size="icon" className="h-8 w-8">
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleOpenCase(); }}>
            <ExternalLink className="w-4 h-4 mr-2" />
            Open Case
          </DropdownMenuItem>
          {task.status !== 'COMPLETED' && (
            <DropdownMenuItem onClick={handleComplete}>
              <CheckCircle className="w-4 h-4 mr-2" />
              Mark Complete
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default function AgentTasks() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState({});
  const [students, setStudents] = useState([]);
  const [activeFilter, setActiveFilter] = useState('pending');
  const [searchQuery, setSearchQuery] = useState('');
  const [studentFilter, setStudentFilter] = useState('all');
  const [serviceFilter, setServiceFilter] = useState('all');
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0 });

  useEffect(() => {
    fetchWorkQueue();
    fetchStudentsList();
  }, [activeFilter]);

  const fetchWorkQueue = async () => {
    try {
      setLoading(true);
      const params = {};

      if (activeFilter === 'today') params.filter = 'today';
      else if (activeFilter === 'overdue') params.filter = 'overdue';
      else if (activeFilter === 'completed') params.filter = 'completed';
      else if (activeFilter === 'pending') params.filter = 'pending';

      const response = await agentAPI.getWorkQueue(params);
      setTasks(response.tasks || []);
      setStats(response.stats || {});
      setPagination(response.pagination || { page: 1, limit: 50, total: 0 });
    } catch (error) {
      console.error('Failed to fetch work queue:', error);
      toast.error('Failed to load work queue');
    } finally {
      setLoading(false);
    }
  };

  const fetchStudentsList = async () => {
    try {
      const response = await agentAPI.getStudentsList();
      setStudents(response.students || []);
    } catch (error) {
      console.error('Failed to fetch students list:', error);
    }
  };

  const handleCompleteTask = async (taskId) => {
    try {
      await agentAPI.completeTask(taskId);
      toast.success('Task marked as complete');
      fetchWorkQueue();
    } catch (error) {
      console.error('Failed to complete task:', error);
      toast.error('Failed to complete task');
    }
  };

  // Filter tasks based on search and filters
  const filteredTasks = useMemo(() => {
    return tasks.filter(task => {
      const studentName = `${task.student?.firstName || ''} ${task.student?.lastName || ''}`.toLowerCase();
      const matchesSearch = !searchQuery ||
        task.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        studentName.includes(searchQuery.toLowerCase());

      const matchesStudent = studentFilter === 'all' || task.student?.studentId === studentFilter;
      const matchesService = serviceFilter === 'all' || task.serviceType === serviceFilter;

      return matchesSearch && matchesStudent && matchesService;
    });
  }, [tasks, searchQuery, studentFilter, serviceFilter]);

  // Group tasks by due date for better visualization
  const groupedTasks = useMemo(() => {
    const today = new Date();
    const groups = {
      overdue: [],
      today: [],
      tomorrow: [],
      thisWeek: [],
      later: [],
      noDue: []
    };

    filteredTasks.forEach(task => {
      if (task.status === 'COMPLETED' && activeFilter === 'completed') {
        groups.later.push(task);
        return;
      }

      if (!task.dueDate) {
        groups.noDue.push(task);
      } else {
        const dueDate = new Date(task.dueDate);
        if (isPast(dueDate) && task.status !== 'COMPLETED') {
          groups.overdue.push(task);
        } else if (isToday(dueDate)) {
          groups.today.push(task);
        } else if (isTomorrow(dueDate)) {
          groups.tomorrow.push(task);
        } else {
          const daysDiff = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));
          if (daysDiff <= 7) {
            groups.thisWeek.push(task);
          } else {
            groups.later.push(task);
          }
        }
      }
    });

    return groups;
  }, [filteredTasks, activeFilter]);

  if (loading) {
    return (
      <AgentLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <Loader2 className="w-12 h-12 text-emerald-500 animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Loading work queue...</p>
          </div>
        </div>
      </AgentLayout>
    );
  }

  return (
    <AgentLayout>
      <div className="space-y-6" data-testid="agent-tasks-page">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-2xl lg:text-3xl font-outfit font-bold text-gray-900">Work Queue</h1>
            <p className="text-gray-600 mt-1">Your daily tasks across all cases</p>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={fetchWorkQueue} variant="outline" disabled={loading}>
              <RefreshCw className={cn("w-4 h-4 mr-2", loading && "animate-spin")} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <Card className={cn(
            "cursor-pointer transition-all hover:shadow-md",
            activeFilter === 'pending' && "ring-2 ring-emerald-500"
          )} onClick={() => setActiveFilter('pending')}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                  <ListTodo className="w-5 h-5 text-gray-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-700">{stats.pending || 0}</p>
                  <p className="text-xs text-gray-500">Pending</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className={cn(
            "cursor-pointer transition-all hover:shadow-md",
            activeFilter === 'today' && "ring-2 ring-emerald-500"
          )} onClick={() => setActiveFilter('today')}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <CalendarClock className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-blue-700">{stats.today || 0}</p>
                  <p className="text-xs text-gray-500">Due Today</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className={cn(
            "cursor-pointer transition-all hover:shadow-md",
            activeFilter === 'overdue' && "ring-2 ring-emerald-500"
          )} onClick={() => setActiveFilter('overdue')}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-red-700">{stats.overdue || 0}</p>
                  <p className="text-xs text-gray-500">Overdue</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-orange-50 to-white border-orange-100">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Flame className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-orange-700">{stats.highPriority || 0}</p>
                  <p className="text-xs text-gray-500">High Priority</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className={cn(
            "cursor-pointer transition-all hover:shadow-md",
            activeFilter === 'completed' && "ring-2 ring-emerald-500"
          )} onClick={() => setActiveFilter('completed')}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-700">{stats.completed || 0}</p>
                  <p className="text-xs text-gray-500">Completed</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-emerald-50 to-white border-emerald-100">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-emerald-700">{stats.total || 0}</p>
                  <p className="text-xs text-gray-500">Total Tasks</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search tasks or students..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          <div className="flex items-center gap-3">
            <Select value={studentFilter} onValueChange={setStudentFilter}>
              <SelectTrigger className="w-48">
                <User className="w-4 h-4 mr-2 text-gray-400" />
                <SelectValue placeholder="Filter by student" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Students</SelectItem>
                {students.map((student) => (
                  <SelectItem key={student.studentId} value={student.studentId}>
                    {student.firstName} {student.lastName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={serviceFilter} onValueChange={setServiceFilter}>
              <SelectTrigger className="w-48">
                <FileText className="w-4 h-4 mr-2 text-gray-400" />
                <SelectValue placeholder="Filter by service" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Services</SelectItem>
                {Object.entries(SERVICE_TYPE_CONFIG).map(([key, config]) => (
                  <SelectItem key={key} value={key}>{config.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Filter Tabs */}
        <Tabs value={activeFilter} onValueChange={setActiveFilter}>
          <TabsList>
            <TabsTrigger value="pending" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white">Pending ({stats.pending || 0})</TabsTrigger>
            <TabsTrigger value="today" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white">Due Today ({stats.today || 0})</TabsTrigger>
            <TabsTrigger value="overdue" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white">Overdue ({stats.overdue || 0})</TabsTrigger>
            <TabsTrigger value="completed" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white">Completed ({stats.completed || 0})</TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Tasks List */}
        {filteredTasks.length === 0 ? (
          <Card className="p-12 text-center">
            <CheckSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">
              {activeFilter === 'completed' ? 'No Completed Tasks' : 'No Tasks Found'}
            </h3>
            <p className="text-gray-600">
              {activeFilter === 'pending' && "Great job! You don't have any pending tasks."}
              {activeFilter === 'today' && "No tasks are due today."}
              {activeFilter === 'overdue' && "Great! You have no overdue tasks."}
              {activeFilter === 'completed' && "No completed tasks yet."}
            </p>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Overdue Section */}
            {groupedTasks.overdue.length > 0 && activeFilter !== 'completed' && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <AlertCircle className="w-5 h-5 text-red-500" />
                  <h3 className="font-semibold text-red-700">Overdue ({groupedTasks.overdue.length})</h3>
                </div>
                <div className="space-y-2">
                  {groupedTasks.overdue.map(task => (
                    <TaskRow
                      key={task.taskId}
                      task={task}
                      onComplete={handleCompleteTask}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Today Section */}
            {groupedTasks.today.length > 0 && activeFilter !== 'completed' && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <CalendarClock className="w-5 h-5 text-orange-500" />
                  <h3 className="font-semibold text-orange-700">Due Today ({groupedTasks.today.length})</h3>
                </div>
                <div className="space-y-2">
                  {groupedTasks.today.map(task => (
                    <TaskRow
                      key={task.taskId}
                      task={task}
                      onComplete={handleCompleteTask}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Tomorrow Section */}
            {groupedTasks.tomorrow.length > 0 && activeFilter !== 'completed' && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Calendar className="w-5 h-5 text-amber-500" />
                  <h3 className="font-semibold text-amber-700">Due Tomorrow ({groupedTasks.tomorrow.length})</h3>
                </div>
                <div className="space-y-2">
                  {groupedTasks.tomorrow.map(task => (
                    <TaskRow
                      key={task.taskId}
                      task={task}
                      onComplete={handleCompleteTask}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* This Week Section */}
            {groupedTasks.thisWeek.length > 0 && activeFilter !== 'completed' && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Calendar className="w-5 h-5 text-blue-500" />
                  <h3 className="font-semibold text-blue-700">This Week ({groupedTasks.thisWeek.length})</h3>
                </div>
                <div className="space-y-2">
                  {groupedTasks.thisWeek.map(task => (
                    <TaskRow
                      key={task.taskId}
                      task={task}
                      onComplete={handleCompleteTask}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Later / Other Tasks Section */}
            {(groupedTasks.later.length > 0 || groupedTasks.noDue.length > 0) && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Clock className="w-5 h-5 text-gray-500" />
                  <h3 className="font-semibold text-gray-700">
                    {activeFilter === 'completed' ? 'Completed Tasks' : 'Later / No Due Date'} ({groupedTasks.later.length + groupedTasks.noDue.length})
                  </h3>
                </div>
                <div className="space-y-2">
                  {[...groupedTasks.later, ...groupedTasks.noDue].map(task => (
                    <TaskRow
                      key={task.taskId}
                      task={task}
                      onComplete={handleCompleteTask}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Pagination Info */}
        {pagination.total > pagination.limit && (
          <div className="text-center text-sm text-gray-500">
            Showing {filteredTasks.length} of {pagination.total} tasks
          </div>
        )}
      </div>
    </AgentLayout>
  );
}
